import { v4 as uuid } from "uuid"

import { SlashID, __INTERNAL_ONLY } from ".."
import { BaseUser } from "../user.base"
import { BaseAnalytics } from "./analytics.base"
import { SlashIDOptions, createSlashIDConfig } from "../../domain/options"
import { createFetchKeepAliveTransport } from "./transport"
import { EventName, EventPostRequest } from "../../clients/slashid-api"
import { ClientGeneratedErrorSlug } from "../../domain/errors.internal"
import {
    IdFlowSucceededEvent,
    IdFlowFailedEvent,
    InvalidPasswordSubmittedEvent,
    IncorrectPasswordSubmittedEvent,
    OtpIncorrectCodeSubmittedEvent,
} from "../../utils/events"

export type BrowserAnalyticsOptions = {
    sdk: SlashID
    user?: BaseUser
}

/**
 * The interface for SlashID user analytics.
 *
 * We've built user analytics right into SlashID. We automatically track user
 * activity surrounding sign-up & log-in so we can give you metrics like monthly
 * active users (MAU), returning users, and new users.
 *
 * Further, we record how people are choosing to authenticate with SlashID and
 * expose this data to you so you can understand user preferences regarding
 * authentication methods, and crucially: any friction caused by requiring that
 * a particular authentication method or combination of authentication methods (MFA)
 * be used.
 */
export class BrowserAnalytics extends BaseAnalytics {
    private readonly sdk: SlashID

    constructor({ sdk, user }: BrowserAnalyticsOptions) {
        const options: SlashIDOptions = {
            oid: sdk.oid,
            baseURL: sdk.baseURL,
            sdkURL: sdk.sdkURL,
            analyticsEnabled: true,
        }
        const config = createSlashIDConfig(options)
        super({ options, user, transport: createFetchKeepAliveTransport(fetch, config) })

        this.sdk = sdk

        this.subscribeToDomainEvents()
        this.trackSdkLoaded()
    }

    protected createCorrelationID() {
        const CORRELATION_ID_STORAGE_KEY = "@slashid/EVENTS_CORRELATION_ID"

        try {
            const storedCorrelationID = sessionStorage.getItem(CORRELATION_ID_STORAGE_KEY)
            if (storedCorrelationID) {
                return storedCorrelationID
            }
        } catch {
            // web storage is not available
        }

        const correlationID = uuid()

        try {
            sessionStorage.setItem(CORRELATION_ID_STORAGE_KEY, correlationID)
        } catch {
            // web storage is not available
        }

        return correlationID
    }

    protected createEventMetadata(
        { location, userAgent }: { location: string; userAgent: string } = {
            location: window.location.href,
            userAgent: navigator.userAgent,
        }
    ) {
        return super.createEventMetadata({ location, userAgent })
    }

    private subscribeToDomainEvents() {
        this.sdk.subscribe("idFlowSucceeded", (e: IdFlowSucceededEvent) => this.trackAuthenticationSuccess(e))
        this.sdk.subscribe("idFlowFailed", (e: IdFlowFailedEvent) => this.trackAuthenticationFailure(e))
        this.sdk.subscribe("invalidPasswordSubmitted", (e: InvalidPasswordSubmittedEvent) =>
            this.trackAuthenticationFailure({ ...e, errorText: "invalid_password" })
        )
        this.sdk.subscribe("incorrectPasswordSubmitted", (e: IncorrectPasswordSubmittedEvent) =>
            this.trackAuthenticationFailure({ ...e, errorText: "incorrect_password" })
        )
        this.sdk.subscribe("otpIncorrectCodeSubmitted", (e: OtpIncorrectCodeSubmittedEvent) => {
            const reason: ClientGeneratedErrorSlug = (() => {
                if (e.factor?.method === "otp_via_email") {
                    return "incorrect_otp_code_via_email"
                }

                if (e.factor?.method === "otp_via_sms") {
                    return "incorrect_otp_code_via_sms"
                }

                return "incorrect_otp_code"
            })()

            this.trackAuthenticationFailure({
                errorText: reason,
                authenticationFactor: { method: e.factor?.method },
                challengeId: e.challengeId,
            })
        })

        const internal = __INTERNAL_ONLY(this.sdk)

        internal.subscribe("clientSideError", (event) => {
            this.trackAuthenticationFailure({
                ...event,
                errorText: "client_side_error",
            })
        })
    }

    private trackAuthenticationSuccess(e: IdFlowSucceededEvent) {
        const user = this.sdk.getUser(e.token)
        this.identify(user)

        const authenticationSucceededEvent = {
            event_name: EventName.AuthenticationSucceededV1,
            person_id: user.ID,
            success_authn_method: e.authenticationFactor?.method!,
            authenticated_methods: user.authentication,
            ...(e.handle && { handle: e.handle }),
        }

        const payload: EventPostRequest = {
            ...this.createEventMetadata(),
            event_data: authenticationSucceededEvent,
        }

        this.sendEvent(payload)
    }

    private trackAuthenticationFailure(e: IdFlowFailedEvent) {
        const user = e.previousToken ? this.sdk.getUser(e.previousToken) : this.user

        const authenticationFailedEvent = {
            event_name: EventName.AuthenticationFailedV1,
            failed_authn_method: e.authenticationFactor?.method!,
            authenticated_methods: user?.authentication,
            failure_reason: e.errorText,
            ...(e.handle && { handle: e.handle }),
            ...(e.challengeId && { challenge_id: e.challengeId }),
            ...(e.failureDetail && { failure_detail: e.failureDetail }),
            ...(user && { person_id: user.ID }),
        }

        const payload: EventPostRequest = {
            ...this.createEventMetadata(),
            event_data: authenticationFailedEvent,
        }

        this.sendEvent(payload)
    }

    private trackSdkLoaded() {
        const payload: EventPostRequest = {
            ...this.createEventMetadata(),
            event_data: {
                event_name: EventName.SlashIdsdkLoadedV1,
            },
        }

        this.sendEvent(payload)
    }

    /**
     * Tracks a virtual page view
     *
     * A virtual page view allows you to track a page view, even when a page is not physically
     * loaded in the browser. For example, when navigating using a client-side router in a
     * single page application, or navigating within a sub-section of a page (like a tabbed pane,
     * or navigation drawer) without changing the page itself.
     *
     * The exact meaning of a virtual page view will vary by implementation, it depends on your
     * routing paradigm and what you're trying to track. You will need to implement this event
     * in a way which makes sense for your product.
     *
     * @param options.url The URL of the page to track as the subject of the virtual page view.
     */
    public trackVirtualPageView(options: { url?: string; userAgent?: string }) {
        const { url = window.location.href, userAgent = navigator.userAgent } = options

        super.trackVirtualPageView({ url, userAgent })
    }
}
